export default function PrivacyPolicy({ onBack }) {
  return (
    <div style={{minHeight:"100dvh",background:"#0f1623",color:"#9aabc2",fontFamily:"system-ui, sans-serif",lineHeight:1.8}}>
      <div style={{maxWidth:640,margin:"0 auto",padding:"40px 24px 80px"}}>
        <button onClick={onBack} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:10,background:"rgba(255,255,255,0.03)",border:"1px solid #1e2a40",color:"#5a7090",fontSize:12,fontFamily:"'DM Mono', monospace",letterSpacing:"0.08em",marginBottom:32,cursor:"pointer"}}>
          ← BACK
        </button>

        <div style={{marginBottom:40}}>
          <div style={{fontSize:10,fontFamily:"'DM Mono', monospace",color:"#4f7cff",letterSpacing:"0.14em",marginBottom:8}}>LEGAL</div>
          <h1 style={{fontSize:28,fontWeight:900,color:"#f0f4ff",fontFamily:"'DM Serif Display', Georgia, serif",marginBottom:8,lineHeight:1.2}}>Privacy Policy</h1>
          <div style={{fontSize:12,color:"#5a7090",fontFamily:"'DM Mono', monospace"}}>Last updated: April 15, 2026</div>
        </div>

        <Section title="Who We Are">
          MyRelay AI ("MyRelay," "we," "us," "our") is an AI-powered telecommunications relay service operated by Frederick Interpreting Agency. Our service enables Deaf, hard-of-hearing, and speech-impaired individuals to make phone calls using text-to-speech and speech-to-text technology. We are committed to protecting your privacy and handling your personal information responsibly.
        </Section>

        <Section title="Information We Collect">
          <B>Account information:</B> When you create an account, we collect your email address and authentication credentials. If you sign in with Google, we receive your name and email from Google's OAuth service.
          {"\n\n"}
          <B>E911 registration data:</B> To comply with FCC regulations, we may collect your full name, phone number, and physical address for emergency service routing purposes.
          {"\n\n"}
          <B>Call metadata:</B> We log call records including the phone number dialed, call duration, timestamps, and billing information. We do not record or store audio from your calls.
          {"\n\n"}
          <B>Payment information:</B> Payments are processed by Stripe. We do not store credit card numbers, CVVs, or full payment details on our servers. Stripe's privacy policy governs how they handle your payment data.
          {"\n\n"}
          <B>Usage data:</B> We collect basic analytics such as feature usage, error logs, and performance metrics to improve our service.
        </Section>

        <Section title="Call Content & FCC Compliance">
          <div style={{background:"rgba(16,185,129,0.06)",border:"1px solid rgba(16,185,129,0.2)",borderRadius:12,padding:"14px 18px",margin:"8px 0 16px"}}>
            <div style={{fontSize:11,fontFamily:"'DM Mono', monospace",color:"#10b981",letterSpacing:"0.1em",marginBottom:6,fontWeight:700}}>FCC §64.604(a)(2) COMPLIANT</div>
            <div style={{fontSize:13,color:"#9aabc2",lineHeight:1.7}}>
              Call transcripts are generated in real time and exist only in your device's temporary memory during an active call. Transcripts are never transmitted to or stored on our servers. When a call ends, all transcript data is permanently deleted. We cannot recover call content after a call has ended.
            </div>
          </div>
          This practice complies with FCC regulations governing Telecommunications Relay Services (TRS), which prohibit relay providers from retaining call content beyond the duration of the call.
        </Section>

        <Section title="How We Use Your Information">
          We use the information we collect to provide and maintain the MyRelay service, process your calls and route them correctly, comply with FCC telecommunications relay requirements, process payments and manage your subscription, send service-related notifications (outages, billing, security), respond to complaints and support requests, and improve the reliability and quality of our service. We do not sell your personal information. We do not use your data for advertising. We do not share call content with third parties.
        </Section>

        <Section title="Third-Party Services">
          MyRelay uses the following third-party services to operate. Each processes data only as needed to perform its function:
          {"\n\n"}
          <B>Twilio</B> — Telephony and call routing{"\n"}
          <B>Deepgram</B> — Real-time speech-to-text transcription{"\n"}
          <B>ElevenLabs</B> — Text-to-speech voice synthesis{"\n"}
          <B>Anthropic (Claude)</B> — AI Agent mode and sentiment analysis{"\n"}
          <B>Supabase</B> — Authentication and database{"\n"}
          <B>Stripe</B> — Payment processing{"\n"}
          <B>Vercel</B> — Frontend hosting
          {"\n\n"}
          Audio streamed to Deepgram and ElevenLabs is processed in real time and not retained by those services after processing. The AI Agent feature sends transcribed call text (not audio) to Anthropic's API for response generation; Anthropic does not use API data for model training.
        </Section>

        <Section title="Data Retention">
          Call transcripts are deleted immediately when each call ends. Account information is retained as long as your account is active. Call metadata (duration, timestamps, billing) is retained for compliance and billing purposes. E911 registration data is retained as required by FCC regulations. If you delete your account, we will remove your personal information within 30 days, except where retention is required by law.
        </Section>

        <Section title="Data Security">
          We protect your information using encryption in transit (TLS/HTTPS) for all data transmission, secure authentication via Supabase Auth, restricted access to production systems and credentials, and no permanent storage of sensitive call content. While no system is perfectly secure, we take reasonable measures to protect your data from unauthorized access, alteration, or disclosure.
        </Section>

        <Section title="Your Rights">
          You may request access to the personal data we hold about you, request correction of inaccurate data, request deletion of your account and associated data, or opt out of non-essential communications. To exercise any of these rights, contact us at{" "}
          <a href="mailto:privacy@myrelay.ai" style={{color:"#4f7cff",textDecoration:"none"}}>privacy@myrelay.ai</a>.
        </Section>

        <Section title="Children's Privacy">
          MyRelay is not directed at children under 13. We do not knowingly collect personal information from children under 13. If we learn we have collected data from a child under 13, we will delete it promptly.
        </Section>

        <Section title="Changes to This Policy">
          We may update this policy from time to time. We will notify you of material changes via email or in-app notification. Continued use of MyRelay after changes constitutes acceptance of the updated policy.
        </Section>

        <Section title="Contact Us">
          For privacy questions or concerns:
          {"\n\n"}
          <B>MyRelay AI</B> — Operated by Frederick Interpreting Agency{"\n"}
          Email: <a href="mailto:privacy@myrelay.ai" style={{color:"#4f7cff",textDecoration:"none"}}>privacy@myrelay.ai</a>{"\n"}
          Support: <a href="mailto:support@myrelay.ai" style={{color:"#4f7cff",textDecoration:"none"}}>support@myrelay.ai</a>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{marginBottom:32}}>
      <h2 style={{fontSize:18,fontWeight:800,color:"#f0f4ff",fontFamily:"'DM Serif Display', Georgia, serif",marginBottom:10,paddingBottom:8,borderBottom:"1px solid #1e2a40"}}>{title}</h2>
      <div style={{fontSize:14,whiteSpace:"pre-wrap"}}>{children}</div>
    </div>
  );
}

function B({ children }) {
  return <span style={{color:"#f0f4ff",fontWeight:600}}>{children}</span>;
}
